import {
  Box,
  Paper,
  Typography,
  Menu,
  MenuItem,
  Tooltip,
  IconButton,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import 'chart.js/auto';
import { Bar } from 'react-chartjs-2';
import { format, eachDayOfInterval, sub } from 'date-fns';
import 'chartjs-adapter-date-fns';
import FilterListIcon from '@mui/icons-material/FilterList';
import useTranslation from '../customHooks/translations';

const LineChart = ({ allSubmittedActions }) => {
  const [lineChartData, setLineChartData] = useState();
  const translation = useTranslation();
  const filters = [
    translation.sevenDays,
    translation.thirtyDays,
    translation.ninetyDays,
    translation.oneYear,
  ];
  const [selectedFilter, setSelectedFilter] = useState(filters[0]);
  const [openFilterMenu, setOpenFilterMenu] = useState(false);
  const [filterMenuAnchor, setFilterMenuAnchor] = useState(null);

  /** functions for filter menu */
  const handleClick = (e) => {
    setFilterMenuAnchor(e.currentTarget);
    setOpenFilterMenu(true);
  };

  const handleClose = () => {
    setFilterMenuAnchor(null);
    setOpenFilterMenu(false);
  };

  /** gets actions within the set date interval for actions with/without image */
  const getLineChartData = () => {
    let startingDate;
    let endingDate;
    let currentDate = new Date();
    if (selectedFilter === filters[0]) {
      startingDate = sub(currentDate, { days: 7 });
      endingDate = currentDate;
    } else if (selectedFilter === filters[1]) {
      startingDate = sub(currentDate, { days: 30 });
      endingDate = currentDate;
    } else if (selectedFilter === filters[2]) {
      startingDate = sub(currentDate, { days: 90 });
      endingDate = currentDate;
    } else if (selectedFilter === filters[3]) {
      startingDate = sub(currentDate, { years: 1 });
      endingDate = currentDate;
    }
    const datesInInterval = eachDayOfInterval({
      start: startingDate,
      end: endingDate,
    });
    const formattedDatesInInterval = datesInInterval.map((date) =>
      format(date, 'yyyy-MM-dd')
    );

    //reduce formattedDatesInInterval array into object with keys being each of the dates
    const formattedDatesInIntervalObject = formattedDatesInInterval.reduce(
      (acc, curr) => {
        acc[curr] = 0;
        return acc;
      },
      {}
    );

    // Group by local calendar date so UTC timestamps don't shift day buckets
    const toLocalDateKey = (value) => {
      if (!value) return '';
      const datePart = String(value).split('T')[0];
      // Stored as plain date-of-action (user-selected calendar day)
      if (/^\d{4}-\d{2}-\d{2}$/.test(datePart) && !String(value).includes('T')) {
        return datePart;
      }
      // Timestamp: use local timezone day
      return format(new Date(value), 'yyyy-MM-dd');
    };

    //group all actions with the same date together
    const co2ValuesPerDay = allSubmittedActions.reduce((prev, curr) => {
      const dateKey = toLocalDateKey(curr.date_of_action);
      (prev[dateKey] = prev[dateKey] || []).push(curr.g_co2_saved);
      return prev;
    }, {});

    //add up all the co2 values for each date in groupedData
    const totalCO2PerDay = Object.fromEntries(
      Object.entries(co2ValuesPerDay).map(([date, values]) => [
        date,
        values.reduce((partialSum, a) => partialSum + a, 0),
      ])
    );

    //if date is in totalCO2PerDay object, replace value of 0 in formattedDatesInIntervalObject with the correct co2 value
    Object.keys(formattedDatesInIntervalObject).forEach(
      (key) =>
        key in totalCO2PerDay &&
        (formattedDatesInIntervalObject[key] = totalCO2PerDay[key])
    );

    setLineChartData(formattedDatesInIntervalObject);
  };

  useEffect(() => {
    getLineChartData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilter]);

  return (
    <Paper sx={{ p: { xs: 2, md: 3 }, width: '100%', minWidth: 0 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 2,
          mb: 3,
          alignItems: 'flex-start',
        }}
      >
        <Typography component="p" variant="subtitle2" sx={{ textAlign: 'left' }}>
          {translation.formatString(translation.co2SavedDuring, selectedFilter)}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Tooltip title={translation.applyFilter}>
            <IconButton onClick={handleClick}>
              <FilterListIcon />
            </IconButton>
          </Tooltip>
          <Menu
            id="filter-menu"
            open={openFilterMenu}
            anchorEl={filterMenuAnchor}
            onClose={handleClose}
          >
            {filters.map((filter, index) => (
              <MenuItem
                key={index}
                onClick={() => {
                  setSelectedFilter(filter);
                  handleClose();
                }}
              >
                {filter}
              </MenuItem>
            ))}
          </Menu>
        </Box>
      </Box>
      {lineChartData && (
        <Box sx={{ height: { xs: 260, md: 320 }, minWidth: 0 }}>
          <Bar
            data={{
              labels: Object.keys(lineChartData),
              datasets: [
                {
                  label: translation.totalGramsSavedCO2PerDayAllActions,
                  data: Object.values(lineChartData),
                  backgroundColor: ['#929cda'],
                  barPercentage: 0.9,
                  categoryPercentage: 0.9,
                  hoverBackgroundColor: '#6c79cd',
                  fill: true,
                },
              ],
            }}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              scales: {
                x: {
                  title: {
                    display: true,
                    text: translation.date,
                    padding: {
                      top: 16,
                    },
                  },
                  type: 'time',
                  time: {
                    unit: 'day',
                    displayFormats: {
                      day: 'MM/dd',
                    },
                    tooltipFormat: 'MMM dd yyyy',
                  },
                },
                y: {
                  beginAtZero: true,
                  title: {
                    display: true,
                    text: translation.gramsCO2Saved,
                    padding: {
                      bottom: 16,
                    },
                  },
                },
              },
              plugins: {
                legend: {
                  display: false,
                },
              },
            }}
          />
        </Box>
      )}
    </Paper>
  );
};

export default LineChart;
